import React from "react";
import {
  BadgeCheck,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  GitCommitHorizontal,
  PackageCheck,
  ShieldCheck,
} from "lucide-react";
import type { AssuranceAssessment, AssuranceLink } from "../../../assurance/types";
import {
  isEvidenceBacked,
  selectAuditDecisions,
  selectCapabilities,
  selectDecision,
  selectGlossary,
  selectSbom,
  selectSyntheticBoundary,
} from "../../../assurance/model";
import {
  CaveatNote,
  EmptyPanel,
  FindingLink,
  MetricTile,
  Pill,
  SectionPanel,
  StatusPill,
  fmtDate,
  fmtDateTime,
  fmtNumber,
} from "../primitives";

// ---------------------------------------------------------------------------
// Report suite
// ---------------------------------------------------------------------------

function linkIcon(kind: string | undefined) {
  switch ((kind || "").toLowerCase()) {
    case "json":
      return FileJson;
    case "csv":
      return FileSpreadsheet;
    case "sbom":
      return PackageCheck;
    default:
      return FileText;
  }
}

const LINK_GROUPS: { title: string; description: string; kinds: string[] }[] = [
  {
    title: "Assurance reports",
    description: "Commit-bound HTML reports rendered by the assurance API",
    kinds: ["report"],
  },
  {
    title: "Data exports",
    description: "Machine-readable assessment and finding exports",
    kinds: ["json", "csv"],
  },
  {
    title: "SBOM artifacts",
    description: "CycloneDX and SPDX inventories for this commit",
    kinds: ["sbom"],
  },
];

export function ReportsView({
  assessment,
  demo,
  endpointLinks,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  endpointLinks: AssuranceLink[];
}) {
  const grouped = LINK_GROUPS.map((group) => ({
    ...group,
    links: endpointLinks.filter((link) =>
      group.kinds.includes((link.kind || "").toLowerCase())
    ),
  }));
  const ungrouped = endpointLinks.filter(
    (link) => !LINK_GROUPS.some((group) => group.kinds.includes((link.kind || "").toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <MetricTile
          label="Available artifacts"
          value={endpointLinks.length ? fmtNumber(endpointLinks.length) : undefined}
          demo={demo}
        />
        <MetricTile
          label="Generated"
          value={fmtDateTime(assessment.generatedAt)}
          demo={demo}
        />
        <MetricTile
          label="Bound to commit"
          value={assessment.repository?.commitShort || assessment.repository?.commit?.slice(0, 7)}
          demo={demo}
        />
      </div>

      {demo && (
        <CaveatNote tone="warning">
          Every artifact below is generated from the synthetic dataset and is
          labeled accordingly. None of them describe a real system.
        </CaveatNote>
      )}

      {grouped.map(
        (group) =>
          group.links.length > 0 && (
            <SectionPanel key={group.title} title={group.title} description={group.description}>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {group.links.map((link) => {
                  const Icon = linkIcon(link.kind);
                  return (
                    <a
                      key={`${link.label}-${link.href}`}
                      href={link.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-3 rounded-lg border border-border/70 bg-surface2/40 px-3.5 py-3 transition-colors hover:border-brand/40 hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand ring-1 ring-brand/20">
                        <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-semibold text-text">
                          {link.label}
                        </span>
                        <span className="block truncate text-[11px] text-muted">
                          {(link.kind || "artifact").toUpperCase()}
                        </span>
                      </span>
                      <Download
                        className="h-4 w-4 flex-none text-muted transition-colors group-hover:text-brand"
                        aria-hidden="true"
                      />
                    </a>
                  );
                })}
              </div>
            </SectionPanel>
          )
      )}

      {ungrouped.length > 0 && (
        <SectionPanel title="Other endpoints">
          <div className="flex flex-wrap gap-2">
            {ungrouped.map((link) => (
              <a
                key={`${link.label}-${link.href}`}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="btn-secondary px-3 py-2 text-xs"
              >
                {link.label}
              </a>
            ))}
          </div>
        </SectionPanel>
      )}

      {!endpointLinks.length && (
        <EmptyPanel
          demo={demo}
          title="No artifacts available"
          detail="The assurance API did not expose downloadable artifacts for this assessment."
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deployment trust
// ---------------------------------------------------------------------------

export function DeploymentView({
  assessment,
  demo,
  onSelectFindingId,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectFindingId: (findingId: string) => void;
}) {
  const decision = selectDecision(assessment);
  const findingById = new Map(
    assessment.findings.map((finding) => [finding.id, finding])
  );
  const sbom = selectSbom(assessment);
  const auditDecisions = selectAuditDecisions(assessment);

  const trustTone =
    decision.state === "pass"
      ? "success"
      : decision.state === "review"
        ? "warning"
        : decision.state === "blocked"
          ? "critical"
          : "neutral";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricTile
          label="Release gate"
          value={
            decision.state === "unknown" ? undefined : (
              <Pill tone={trustTone} icon={<ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />} className="px-2.5 py-1 text-sm">
                {decision.state === "blocked"
                  ? "Blocked"
                  : decision.state === "review"
                    ? "Review"
                    : "Passing"}
              </Pill>
            )
          }
          sub={
            decision.total
              ? `${decision.failing} failing of ${decision.total} gates`
              : undefined
          }
          demo={demo}
        />
        <MetricTile
          label="Artifact provenance"
          value={
            sbom ? (
              <Pill
                tone={sbom.commitBound ? "success" : "warning"}
                icon={<GitCommitHorizontal className="h-3.5 w-3.5" aria-hidden="true" />}
                className="px-2.5 py-1 text-sm"
              >
                {sbom.commitBound ? "Commit-bound SBOM" : "SBOM recorded"}
              </Pill>
            ) : undefined
          }
          sub={sbom?.repositoryCommit ? `commit ${sbom.repositoryCommit.slice(0, 7)}` : undefined}
          demo={demo}
        />
        <MetricTile
          label="Environment validation"
          value={undefined}
          sub={demo ? "no runtime environment assessed" : "Runtime state not collected"}
          demo={demo}
        />
      </div>

      <SectionPanel
        title="Accepted risks & exceptions"
        description="Formally recorded decisions that modify the release gate"
        flush
      >
        {auditDecisions.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Decision</th>
                  <th scope="col">Finding</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">Approver</th>
                  <th scope="col">Decided</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Compensating controls</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {auditDecisions.map((audit, index) => (
                  <tr key={audit.id || index}>
                    <td className="whitespace-nowrap font-mono text-xs text-muted">{audit.id}</td>
                    <td className="whitespace-nowrap">
                      {audit.findingId ? (
                        <FindingLink
                          id={audit.findingId}
                          finding={findingById.get(audit.findingId)}
                          onOpen={(item) => onSelectFindingId(item.id)}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap font-medium text-text">{audit.decision || "—"}</td>
                    <td className="max-w-[180px] truncate">{audit.approver || "—"}</td>
                    <td className="whitespace-nowrap text-muted">{fmtDate(audit.decidedAt) || "—"}</td>
                    <td className="whitespace-nowrap text-muted">{fmtDate(audit.expiresAt) || "—"}</td>
                    <td className="max-w-xs text-muted">
                      <span className="line-clamp-2">{audit.compensatingControls || "—"}</span>
                    </td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={audit.status} demo={demo} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel
              demo={demo}
              icon={<ShieldCheck className="h-7 w-7 text-success" aria-hidden="true" />}
              title="No accepted-risk exceptions"
              detail="No findings have been suppressed or waived — every open finding still carries an active remediation expectation."
            />
          </div>
        )}
      </SectionPanel>

      <CaveatNote>
        Deployment trust connects repository evidence (policy gates, SBOM
        provenance, recorded exceptions) to the release decision. It does not
        attest to runtime environments, admission control, or production
        configuration{demo ? " — none were assessed in this synthetic dataset" : ""}.
      </CaveatNote>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Replacement proof-of-value
// ---------------------------------------------------------------------------

export function ReplacementView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const capabilities = selectCapabilities(assessment);
  const boundary = selectSyntheticBoundary(assessment);
  const glossary = selectGlossary(assessment);
  const backed = capabilities.filter((row) => isEvidenceBacked(row.status)).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <MetricTile
          label="Criteria demonstrated"
          value={capabilities.length ? fmtNumber(backed) : undefined}
          tone="success"
          demo={demo}
        />
        <MetricTile
          label="Pending validation"
          value={capabilities.length ? fmtNumber(capabilities.length - backed) : undefined}
          tone={capabilities.length - backed > 0 ? "warning" : "success"}
          demo={demo}
        />
        <MetricTile
          label="Decision state"
          value={
            <Pill tone="warning" icon={<BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />} className="px-2.5 py-1 text-sm">
              Requires client validation
            </Pill>
          }
          demo={demo}
        />
      </div>

      {boundary.evidenceBoundary && (
        <SectionPanel title="Evidence boundary" description="What this evaluation does and does not claim">
          <p className="text-[13px] leading-6 text-text2">{boundary.evidenceBoundary}</p>
          {boundary.parityCaveat && (
            <div className="mt-3">
              <CaveatNote tone="warning">{boundary.parityCaveat}</CaveatNote>
            </div>
          )}
        </SectionPanel>
      )}

      <SectionPanel
        title="Replacement criteria"
        description="Capability-by-capability position, stated without overclaiming"
        flush
      >
        {capabilities.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Criterion</th>
                  <th scope="col">Current evidence</th>
                  <th scope="col">Status</th>
                  <th scope="col">Validation required</th>
                </tr>
              </thead>
              <tbody>
                {capabilities.map((row, index) => (
                  <tr key={row.capability || index}>
                    <td className="max-w-[220px] font-medium text-text">{row.capability}</td>
                    <td className="max-w-md">{row.evidence || "—"}</td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={row.status} demo={demo} />
                    </td>
                    <td className="max-w-sm text-muted">{row.limitation || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>

      <SectionPanel
        title="Recommended next step"
        description="The claim this evaluation supports"
      >
        <p className="max-w-3xl text-[13px] leading-6 text-text2">
          This evaluation demonstrates a coherent evidence-to-decision operating
          model. A bounded proof-of-value on the client's own repositories is
          required to validate precision, ecosystem coverage, deployment trust,
          developer workflow fit, report usefulness, and the commercial
          replacement case.
        </p>
      </SectionPanel>

      {glossary.length > 0 && (
        <SectionPanel title="Evidence glossary" description="How terms are used across this workspace">
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {glossary.map((entry, index) => (
              <div key={entry.term || index}>
                <dt className="text-xs font-semibold text-text">{entry.term}</dt>
                <dd className="mt-0.5 text-xs leading-5 text-muted">{entry.definition}</dd>
              </div>
            ))}
          </dl>
        </SectionPanel>
      )}
    </div>
  );
}

